import { useTranslate } from '@tolgee/react';
import { DialogueVariationCardMessage } from 'components/Dialogue/types';
import { useChatContext } from '../../contexts/ChatContext';
import { useContext, useMemo, useRef } from 'react';
import { useInViewport } from 'ahooks';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import { useQuery } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey.ts';
import { productList, ProductListParams } from 'services/api/conversation.ts';
import SafeImage from 'components/SafeImage/index.tsx';
import { formatNumber } from 'utils/numberFormat';
import { Button, Divider, message, Skeleton, Space } from 'antd';
import SellerCenter from 'components/SellerCenter/index.tsx';
import { goItemSellerCenter } from 'views/Conversation/Sider/ProductComponent.tsx';
import Icon from '@ant-design/icons';
import { ReactComponent as LinkSvg } from 'assets/icons/link.svg';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable.ts';
import toLowerCase from 'utils/toLowerCase.ts';
import { openExternalURL } from 'utils/openExternalURL.ts';
import { MessageExtend } from '../MessageExtend/index.tsx';
import { MessageStatusDecorate } from '../MessageStatusDecorate.tsx';
import styled from '@emotion/styled';

function VariationCardMsg({
  conversation,
  msg,
  raw,
}: {
  conversation: Conversation.Conversation;
  msg: DialogueVariationCardMessage;
  raw: Conversation.Message;
}) {
  const { t } = useTranslate();

  const { dialogRef } = useChatContext();

  const ref = useRef<HTMLDivElement>(null);

  const [inViewPort = false] = useInViewport(ref, {
    root: dialogRef?.dom,
  });

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const { channel, region, buyerId, storeId } = conversation;
  const {
    content: { productId, modelId },
    failCause,
  } = msg;

  const xiapibuyEnable = useXiapibuyEnable(region);

  const queryParams = useMemo<ProductListParams>(
    () => ({
      page_size: 1,
      page_no: 1,
      internal_store_id: storeId,
      keyword: productId,
      internal_buyer_id: buyerId,
      keyword_type: 1,
    }),
    [storeId, productId, buyerId],
  );

  const { data: product } = useQuery({
    enabled: inViewPort || isFaq,
    staleTime: 3 * 60 * 1000,
    queryKey: [ApiQueryKey.ProductList, queryParams],
    queryFn: ({ queryKey, signal }) =>
      productList(queryKey[1] as ProductListParams, signal).then((res) => {
        return res.itemList?.[0];
      }),
  });

  return (
    <div ref={ref}>
      <MessageExtend
        raw={raw}
        msg={msg}
        conversation={conversation}
        content={
          <MessageStatusDecorate
            message={raw}
            status={msg?.status}
            content={(() => {
              if (!product) {
                return (
                  <Container>
                    <header>{t('domain.message.type.variationCard')}</header>
                    <div>
                      <Skeleton.Image />
                      <section>
                        <Skeleton.Input active={inViewPort} size="small" />
                        <Space>
                          <Skeleton.Button active={inViewPort} size="small" />
                          <Skeleton.Button active={inViewPort} size="small" />
                        </Space>
                      </section>
                    </div>
                    <footer>
                      <Skeleton.Button active={inViewPort} size="small" />
                    </footer>
                  </Container>
                );
              }

              const { itemName, skuInfo, itemUrl, unit, externalShopId } = product;

              const targetSku = skuInfo.find(({ skuId }) => skuId === modelId);

              let url = itemUrl;
              if (channel === 'SHOPEE') {
                if (xiapibuyEnable && region && url && !url.includes('.xiapibuy.com')) {
                  url = `https://${toLowerCase(
                    region,
                  )}.xiapibuy.com/product/${externalShopId}/${productId}`;
                }
              }

              if (targetSku) {
                const { skuPic, price, skuName, sellerSku } = targetSku;
                return (
                  <Container>
                    <header>{t('domain.message.type.variationCard')}</header>
                    <div onClick={() => openExternalURL(url, '__blank')}>
                      <SafeImage src={skuPic} width={56} />
                      <section>
                        <p>{itemName}</p>
                        <div>
                          {unit}
                          {formatNumber(price, '0,0.00')}
                          <Divider type="vertical" />
                          {skuName}
                          <Divider type="vertical" />
                          {sellerSku}
                        </div>
                      </section>
                    </div>
                    <footer>
                      <SellerCenter platform={conversation.channel}>
                        <Button
                          type="text"
                          onClick={() => {
                            goItemSellerCenter(
                              conversation.storeId,
                              productId,
                              conversation.channel,
                            ).catch(() => {
                              message.error(
                                t('scene.chatWorkspace.orderTab.error.openSellerCenterFailed'),
                              );
                            });
                          }}
                        >
                          {t('scene.chatWorkspace.orderTab.action.viewSellerCenter')}
                          <Icon component={LinkSvg} style={{ marginLeft: 4 }} />
                        </Button>
                      </SellerCenter>
                    </footer>
                  </Container>
                );
              }

              const { imageUrl, skuLowPrice, itemSku } = product;

              return (
                <Container>
                  <header>{t('domain.message.type.variationCard')}</header>
                  <div onClick={() => openExternalURL(url, '__blank')}>
                    <SafeImage src={imageUrl[0]} width={56} />
                    <section>
                      <p>{itemName}</p>
                      <div>
                        {unit}
                        {formatNumber(skuLowPrice, '0,0.00')}
                        <Divider type="vertical" />
                        {itemSku}
                      </div>
                    </section>
                  </div>
                  <footer>
                    <SellerCenter platform={conversation.channel}>
                      <Button
                        type="text"
                        onClick={() => {
                          goItemSellerCenter(
                            conversation.storeId,
                            productId,
                            conversation.channel,
                          ).catch(() => {
                            message.error(
                              t('scene.chatWorkspace.orderTab.error.openSellerCenterFailed'),
                            );
                          });
                        }}
                      >
                        {t('scene.chatWorkspace.orderTab.action.viewSellerCenter')}
                        <Icon component={LinkSvg} style={{ marginLeft: 4 }} />
                      </Button>
                    </SellerCenter>
                  </footer>
                </Container>
              );
            })()}
            failCause={failCause}
            dialogRef={dialogRef}
          />
        }
      />
    </div>
  );
}

const Container = styled.div`
  width: 410px;
  padding: 8px 12px 0;
  background: var(--fett-bg-panel);
  border-radius: 8px;
  > header {
    font-weight: 500;
    margin-bottom: 12px;
  }
  > div {
    display: flex;
    > div {
      flex: 0 0 auto;
      margin-right: 12px;
    }
    > section {
      flex: 1 0 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      > p {
        margin-bottom: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      > div {
        font-size: 12px;
        color: #999999;
      }
    }
  }
  > footer {
    display: flex;
    justify-content: flex-end;
    border-top: dashed 1px #d1d0d0;
    [data-color-mode='dark'] & {
      border-top-color: var(--fett-border-default);
    }
  }
  .ant-skeleton-image {
    width: 56px;
    height: 56px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
  .ant-skeleton-input {
    width: 100%;
  }
  .ant-skeleton-button {
    margin: 8px 0;
  }
`;

export default VariationCardMsg;
